import React, { useEffect, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import axios from 'axios';
import { Editor, EditorProvider } from 'react-simple-wysiwyg';
import notify from '../../../utils/notify';
import { api } from '../../../config';
import { getAuthToken } from '../../../auth/tokenvalidation';
import ActionButton from '../../../Components/Common/ActionButton';
import {
  FormField,
  FormGrid,
  RequiredNote,
  TextField,
  focusFirstError,
  hasErrors,
  required,
  validateForm,
} from '../../../Components/Form';
import RichTextToolbar from './RichTextToolbar';

const isBlankHtml = value =>
  !String(value ?? '')
    .replace(/<br\s*\/?>|&nbsp;|<[^>]*>/gi, '')
    .trim();

const TEMPLATE_SCHEMA = {
  name: { label: 'Template name', rules: [required()] },
  template: {
    label: 'Template',
    rules: [value => (isBlankHtml(value) ? 'Template is required.' : null)],
  },
};

// axios interceptors may or may not have unwrapped the ApiResponse already
const unwrap = response => response?.data?.data ?? response?.data ?? response;

/**
 * Saves a new answer template for one clause index to master data
 * (Document Template, POST /template-procurement-document) straight from the
 * Requirement tab. The editor starts with the clause's current wording.
 */
const ClauseTemplateModal = ({
  isOpen,
  toggle,
  clauseDetailId,
  clauseTitle,
  initialTemplate = '',
  onSaved,
}) => {
  const [formData, setFormData] = useState({ name: '', template: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setFormData({ name: '', template: initialTemplate || '' });
      setErrors({});
    }
    // only prefill when the modal opens, not while the clause is edited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    if (saving) return;
    toggle();
  };

  const handleSave = async () => {
    const nextErrors = validateForm(formData, TEMPLATE_SCHEMA);
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) {
      focusFirstError(nextErrors, ['name', 'template']);
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(
        `${api.API_URL}/template-procurement-document`,
        {
          name: formData.name.trim(),
          template: formData.template,
          clauseDetailId,
        },
        {
          withXSRFToken: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      );
      notify.success('Template saved to master data.');
      onSaved?.(unwrap(response));
      toggle();
    } catch (error) {
      notify.apiError(error, 'save the template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered size='lg'>
      <ModalHeader toggle={handleClose} className='fw-semibold'>
        New Answer Template
      </ModalHeader>
      <ModalBody>
        <p className='text-muted fs-12 mb-3'>
          For clause <strong>{clauseTitle}</strong>. The template is saved to
          master Document Template and can be inserted into this clause on any
          procurement.
        </p>
        <RequiredNote />
        <FormGrid columns={1}>
          <TextField
            label='Template name'
            name='name'
            required
            placeholder='e.g. Standard force majeure wording'
            value={formData.name}
            error={errors.name}
            onChange={e => {
              setFormData(prev => ({ ...prev, name: e.target.value }));
              setErrors(prev => ({ ...prev, name: '' }));
            }}
          />
          <FormField
            label='Template'
            name='template'
            required
            error={errors.template}
            group
          >
            {() => (
              <div className='snow-editor'>
                <EditorProvider>
                  <Editor
                    id='template'
                    name='template'
                    placeholder='Write the answer template'
                    value={formData.template}
                    onChange={e => {
                      setFormData(prev => ({
                        ...prev,
                        template: e.target.value,
                      }));
                      setErrors(prev => ({ ...prev, template: '' }));
                    }}
                    style={{ height: '260px', overflowY: 'auto' }}
                    className='form-control'
                  >
                    <RichTextToolbar highlight />
                  </Editor>
                </EditorProvider>
              </div>
            )}
          </FormField>
        </FormGrid>
      </ModalBody>
      <ModalFooter>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-close-line'
          disabled={saving}
          onClick={handleClose}
        >
          Close
        </ActionButton>
        <ActionButton
          variant='primary'
          size='sm'
          icon='ri-save-line'
          loading={saving}
          onClick={handleSave}
        >
          Save template
        </ActionButton>
      </ModalFooter>
    </Modal>
  );
};

export default ClauseTemplateModal;
